"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, Transition, motion } from "motion/react";
import {
  Children,
  cloneElement,
  ReactElement,
  useState,
  useId,
} from "react";

export type AnimatedBackgroundProps = {
  children:
    | (ReactElement<{ "data-id": string }> | null)[]
    | ReactElement<{ "data-id": string }>;
  defaultValue?: string;
  onValueChange?: (newActiveId: string | null) => void;
  className?: string;
  transition?: Transition;
  enableHover?: boolean;
};

export function AnimatedBackground({
  children,
  defaultValue,
  onValueChange,
  className,
  transition,
  enableHover = false,
}: AnimatedBackgroundProps) {
  const [activeId, setActiveId] = useState<string | null>(defaultValue ?? null);
  const [previousDefaultValue, setPreviousDefaultValue] = useState(defaultValue);
  if (previousDefaultValue !== defaultValue) {
    setPreviousDefaultValue(defaultValue);
    if (defaultValue !== undefined) setActiveId(defaultValue);
  }
  const uniqueId = useId();

  const handleSetActiveId = (id: string | null) => {
    setActiveId(id);

    if (onValueChange) {
      onValueChange(id);
    }
  };

  return Children.map(children, (child, index) => {
    // Skip null children
    if (!child) return null;

    // Type assertion needed because children props are inferred as unknown
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const id = (child.props as any)["data-id"];

    const interactionProps = enableHover
      ? {
          onMouseEnter: () => handleSetActiveId(id),
          onMouseLeave: () => handleSetActiveId(null),
        }
      : {
          onClick: () => handleSetActiveId(id),
        };

    return cloneElement(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      child as any,
      {
        key: index,
        className: cn(
          "relative inline-flex",
          // Type assertion needed for className access
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (child.props as any).className
        ),
        "data-checked": activeId === id ? "true" : "false",
        ...interactionProps,
      } as Record<string, unknown>,
      <>
        <AnimatePresence initial={false}>
          {activeId === id && (
            <motion.div
              layoutId={`background-${uniqueId}`}
              className={cn("absolute inset-0", className)}
              transition={transition}
              initial={{ opacity: defaultValue ? 1 : 0 }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
            />
          )}
        </AnimatePresence>
        <div className="z-10 relative">
          {/* Type assertion needed for children access */}
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {(child.props as any).children}
        </div>
      </>
    );
  });
}
